import React, { useId } from 'react';
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  /** Optional content rendered to the right of the label, e.g. counters. */
  labelAside?: React.ReactNode;
  error?: string;
  helperText?: React.ReactNode;
  leftIcon?: React.ReactNode;
  /** Optional content rendered inside the input, anchored to the right (e.g. counters). */
  rightAdornment?: React.ReactNode;
  required?: boolean;
}
export function Input({
  label,
  labelAside,
  error,
  helperText,
  leftIcon,
  rightAdornment,
  required,
  className = '',
  id,
  ...props
}: InputProps) {
  const reactId = useId();
  const inputId = id || props.name || `inp-${reactId}`;
  const helperId = helperText ? `${inputId}-help` : undefined;
  const errorId = error ? `${inputId}-err` : undefined;
  const describedBy = [errorId, helperId].filter(Boolean).join(' ') || undefined;
  return <div className="w-full" data-id="element-987">
      {label && <div className="flex items-baseline justify-between mb-1.5 gap-3" data-id="element-988">
          <label htmlFor={inputId} className="block text-sm font-medium text-gray-900" data-id="element-989">
            {label}
            {required && <span className="text-red-600 ml-0.5" aria-hidden="true" data-id="element-990">
                *
              </span>}
            {required && <span className="sr-only" data-id="element-991"> (required)</span>}
          </label>
          {labelAside && <span className="text-xs text-gray-600 tabular-nums" data-id="element-992">
              {labelAside}
            </span>}
        </div>}
      <div className="relative" data-id="element-993">
        {leftIcon && <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-600" data-id="element-994">
            {leftIcon}
          </div>}
        <input id={inputId} required={required} aria-invalid={error ? true : undefined} aria-describedby={describedBy} className={`
            block w-full min-h-[44px] rounded-lg border-2 shadow-sm bg-white
            text-gray-900 placeholder-gray-500
            focus:outline-none focus:border-[#B8860B] focus:ring-2 focus:ring-[#B8860B] focus:ring-offset-1
            disabled:bg-gray-100 disabled:text-gray-600 disabled:border-gray-300
            sm:text-sm py-3
            ${leftIcon ? 'pl-10' : 'pl-3'}
            ${rightAdornment ? 'pr-16' : 'pr-3'}
            ${error ? 'border-red-600 focus:border-red-700 focus:ring-red-600' : 'border-gray-500'}
            ${className}
          `} {...props} data-id="element-995" />
        {rightAdornment && <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-xs text-gray-600 tabular-nums pointer-events-none" data-id="element-996">
            {rightAdornment}
          </div>}
      </div>
      {helperText && !error && <p id={helperId} className="mt-1 text-xs text-gray-600" data-id="element-997">
          {helperText}
        </p>}
      {error && <p id={errorId} className="mt-1 text-sm text-red-700 font-medium" role="alert" data-id="element-998">
          {error}
        </p>}
    </div>;
}